/**
 * The Case Prep workflows and their fixed prompts. A prompt names its
 * template and version so the transcript says which wording ran; change the
 * wording and the version goes up. Each prompt keeps Casey on the paths that
 * apply without an approval (organize without preview, titles with
 * `apply=true`, the label style left alone, `generate_packet`), and names
 * the calls to make first: the server runs a stamped step without the skill
 * listing or off-task tools, on the step's own model
 * (`caseblink/case_agent/workflow_turn.py`).
 */
import {
  BookOpen,
  ClipboardList,
  FileText,
  FolderTree,
  ListChecks,
  ListOrdered,
  ListTodo,
  Package,
  PenLine,
  TableOfContents
} from "lucide-react";

import { formsLine } from "./forms";
import type {
  FormChoice,
  LetterChoice,
  PromptContext,
  WorkflowDef,
  WorkflowGroup,
  WorkflowId,
  WorkflowLane,
  WorkflowScope,
  WorkflowSettings
} from "./types";
import type { ResearchTopic } from "./researchTopics";

export const WORKFLOW_THREAD_TITLE = "CaseBlink Workflow";

/** Each lane's thread: Run Everything's side-by-side steps run on their own. */
export const WORKFLOW_LANE_TITLES: Record<WorkflowLane, string> = {
  main: WORKFLOW_THREAD_TITLE,
  research: `${WORKFLOW_THREAD_TITLE} · Research`,
  forms: `${WORKFLOW_THREAD_TITLE} · Forms`
};

/** Board order: Retitle Exhibits, then Document Organization. */
export const WORKFLOW_GROUPS: readonly WorkflowGroup[] = [
  { id: "exhibits", title: "Exhibit Preparation" },
  { id: "documents", title: "Document Organization" },
  { id: "analysis", title: "Analysis" },
  { id: "drafting", title: "Drafting" },
  { id: "filing", title: "Filing" }
];

const visaOf = (ctx: PromptContext) => ctx.settings.visa || "this visa";

const DEFAULT_MARK = " (CaseBlink default)";

/** The title template as a prompt names it, with where it came from. */
const templateOf = (s: WorkflowSettings) =>
  s.titleTemplateIsFirm
    ? `the firm template "${s.titleTemplate}"`
    : "CaseBlink's default title format for each document type (the firm hasn't saved its own)";

/** How letters cite exhibits, with where it came from. */
const citationOf = (s: WorkflowSettings) =>
  s.citationIsFirm
    ? s.citation
    : `${s.citation}, CaseBlink's built-in citation style`;

/** Which template each letter starts from: the firm's marked defaults, else Firm Instructions. */
const templatesOf = (ctx: PromptContext) => {
  const picks = ctx.settings.defaultTemplates;
  if (!picks.length)
    return `The firm hasn't marked a default template for ${visaOf(
      ctx
    )} letters, so follow Firm Instructions and pick from the firm's templates in Firm Knowledge.`;
  const list = picks
    .map((p) => `the ${p.label} from "${p.name}" (library entry ${p.entryId})`)
    .join("; ");
  return (
    `Start each letter from the firm's default template for its type and ${visaOf(
      ctx
    )}: ${list}. Name that template in generate_draft's instructions. ` +
    "For a letter without a default, follow Firm Instructions and pick from the firm's templates in Firm Knowledge."
  );
};

/** A template name as a scope clause can carry it: no sentence or clause breaks. */
const inClause = (name: string) =>
  name
    .replace(/;/g, ",")
    .replace(/\.(\s|$)/g, "$1")
    .trim();

/** "Petition Letter (Z&Z O-1 Template), Cover Letter": the chat's summary line. */
export const lettersLine = (letters: LetterChoice[]): string =>
  letters
    .map((l) =>
      l.template ? `${l.label} (${inClause(l.template.name)})` : l.label
    )
    .join(", ");

const letterSource = (l: LetterChoice) =>
  l.template
    ? `${l.draftType} (${l.label}) from the firm template "${l.template.name}" (library entry ${l.template.entryId}), named in generate_draft's instructions`
    : `${l.draftType} (${l.label}) from CaseBlink's standard template, with no firm template`;

const noteOf = (ctx: PromptContext) => {
  const note = ctx.note.trim();
  return note ? ` For this case: ${note}` : "";
};

const plural = (n: number, one: string, many = `${one}s`) =>
  `${n} ${n === 1 ? one : many}`;

/** Fill Forms with no picks: the forms the visa calls for. */
const visaForms = (ctx: PromptContext) =>
  `Scope: the USCIS forms a ${visaOf(ctx)} filing needs plus the G-28. ` +
  "Call list_form_catalog and list_forms first, in one step as parallel calls. " +
  "Create any that are missing with create_form, all in one step, then fill every form in one step as parallel fill_form(form_id) calls";

const formStep = (f: FormChoice) =>
  f.formId
    ? `${f.name}: the case's form ${f.formId}`
    : `${f.name}: create_form(form_number="${f.formNumber}"${
        f.label ? `, label="${f.label}"` : ""
      })`;

/** Fill Forms' picked copies, each its own form on the case. */
const pickedForms = (forms: FormChoice[]) =>
  `Scope: ${plural(forms.length, "form")}, one document each: ${formsLine(
    forms
  )}. ` +
  `Fill exactly these forms and no others: ${forms
    .map(formStep)
    .join("; ")}. ` +
  "Call list_forms first. Create the ones not on the case with create_form exactly as written, all in one step as parallel calls: a label makes a copy its own form, so never fill one copy in place of another. " +
  "Then fill every form in one step as parallel fill_form(form_id) calls, one per copy";

const selectionIds = (scope: WorkflowScope) =>
  scope.kind === "selection" ? scope.nodeIds.join(", ") : "";

/** The organizer reads every file and the firm's usual layout itself. */
const ORGANIZER_READS =
  "Call it first: it reads every file and learns the firm's usual layout from its filed cases itself, so don't list the tree, read files, the case summary or any skill before it.";

/**
 * The organizer reads the firm's folder preferences itself and its
 * instruction only steers the classifiers, so the one instruction is this
 * case's note.
 */
const organizerCall = (ctx: PromptContext, target: string) => {
  const note = ctx.note.trim();
  return [
    `Call start_autocategorize with ${target}, preview off and run_in_background off so the moves apply in this turn with Undo; don't ask me to approve a preview.`,
    note
      ? "Pass this case's note below as its instruction, word for word, and nothing else."
      : "Pass it no instruction, not even a restatement of this request.",
    `${ORGANIZER_READS}${
      note ? `\nNote for this case:\n"""\n${note}\n"""\n` : ""
    }`
  ].join(" ");
};

const ORGANIZE_REPORT =
  "Then reply in one or two sentences: how many files moved and into how many folders, and any folders removed. If it put files in Unclassified (needs_review rows with that folder), say so and list each by name with its reason, one line each; mention the other flagged files in a phrase. Don't list the moves: the run card shows each one.";

const ORGANIZE_END =
  "After that reply, the turn ends. Don't move, rename or delete anything yourself, and don't retitle, renumber, draft or build the packet, update Case Insights, confirm key facts or ask me questions in this turn.";

const INSIGHTS_END =
  "After that reply, the turn ends. Don't retitle, move, rename, renumber or delete files, and don't draft or build the packet, or ask me questions in this turn.";

/**
 * The turn that proposes Case Insights from the files and leaves them for
 * the attorney to confirm. Organize and Retitle still forbid updating them.
 */
const insightsBody = (ctx: PromptContext) =>
  [
    "Scope: this case's Case Insights.",
    "Propose values from the organized files — name, dates, employer, visa facts and the rest this case type keeps — and leave them for the attorney to confirm, the way Case Strategy used to.",
    "Call key_facts(refresh=True, wait_s=60) first so Case Insights reads the files and writes proposed values. If values are still checking or a reconcile is still running, call key_facts(wait_s=60) again and review what comes back; don't work out a value yourself.",
    "Reply in one or two sentences: which values are now proposed from the files, which are already confirmed, and which still need the attorney. Name each one that needs the attorney (proposed and awaiting review, changed, needs input, or a must flag) in one line.",
    "Don't confirm, override or dismiss a value the attorney hasn't chosen. Update an insight only with update_key_fact, and only for sync_fields when a draft's fact field is stale.",
    INSIGHTS_END
  ].join(" ") + noteOf(ctx);

const INCONSISTENCIES_END =
  "After that reply, the turn ends. Don't retitle, move, rename, renumber or delete files, and don't draft or build the packet, confirm insights or ask me questions in this turn.";

const inconsistenciesBody = (ctx: PromptContext) =>
  [
    "Scope: this case's files, Case Insights, drafts and forms.",
    "Find facts that disagree: names, dates of birth, employers, job titles, addresses, visa class, marital status, a form answer that doesn't match a file, an exhibit title that doesn't match the document.",
    "Call key_facts first, then list_drafts and list_forms in one step as parallel calls. Read only a file or draft that disagrees.",
    "If nothing disagrees, reply that the record is consistent and stop.",
    'If something disagrees, call create_plan once: title "Case Inconsistencies", overview naming how many clashes, markdown a short list of each (what says A, what says B, where). todos is one item per clash, owner the attorney, each a concrete fix (confirm which date is right, replace the unsigned lease). Do not tell the attorney to click Build: the todos are their checklist.',
    'End the reply with Open questions: and one "- " line per clash, same wording as the todos, so the card lists them.',
    INCONSISTENCIES_END
  ].join(" ") + noteOf(ctx);

const organizeBody = (ctx: PromptContext) => {
  const visa = visaOf(ctx);
  if (ctx.scope.kind === "selection")
    return [
      `Scope: the ${plural(
        ctx.scope.nodeIds.length,
        "selected file"
      )} (node ids ${selectionIds(ctx.scope)}).`,
      `Move them, wherever they are now, into the folder structure this firm uses for ${visa} cases.`,
      organizerCall(ctx, `node_ids [${selectionIds(ctx.scope)}]`),
      ORGANIZE_REPORT,
      ORGANIZE_END
    ].join(" ");
  return [
    `Scope: all ${plural(
      ctx.files,
      "file"
    )} in Case Files, including files already in folders.`,
    `Reorganize the whole case into the folder structure this firm uses for ${visa} cases; files already in folders may move.`,
    organizerCall(ctx, 'scope "all"'),
    ORGANIZE_REPORT,
    ORGANIZE_END
  ].join(" ");
};

/** The files a retitle covers, and the scope sentence the chat reads back. */
const retitleTarget = (ctx: PromptContext) => {
  const { scope } = ctx;
  if (scope.kind === "selection") {
    const byId = new Map(ctx.allFiles.map((f) => [f.id, f]));
    return {
      files: scope.nodeIds.map((id) => byId.get(id) ?? { id, name: id }),
      sentence: `Scope: the ${plural(scope.nodeIds.length, "selected file")}.`
    };
  }
  if (scope.kind === "uploadNames")
    return {
      files: ctx.untitled,
      sentence: `Scope: only the ${plural(
        ctx.untitled.length,
        "file"
      )} still named as uploaded. These are exactly the files whose current name equals their original upload name; leave every other file's title as it is.`
    };
  return {
    files: ctx.allFiles,
    sentence: `Scope: all ${plural(
      ctx.allFiles.length,
      "file"
    )}, replacing titles. Retitle every file in Case Files, including titles renamed by hand, replacing the titles they have now; only a title a person explicitly kept stays. The change card carries the Undo.`
  };
};

const RETITLE_REPORT =
  "Then reply in one or two sentences from the results: how many files got new titles, and any still without one and why. Don't list the titles: the change card shows each file's old and new title.";
const RETITLE_END =
  "After that reply, the turn ends. Don't move, renumber or delete files, draft or build the packet, update Case Insights, confirm key facts or ask me questions in this turn.";

/**
 * One call titles and writes every file; the backend checks shared titles
 * and upload names left behind and lists them in `needs_work`. Casey only
 * fixes those and, when the firm has them, its title instructions.
 */
const retitleBody = (ctx: PromptContext) => {
  const { files, sentence } = retitleTarget(ctx);
  const ids = files.map((f) => f.id).join(", ");
  const instructions = ctx.settings.titleInstructions;
  return [
    sentence,
    `Call propose_exhibit_titles once, with scope "node_ids" and node_ids [${ids}], apply=true, min_confidence="low" and run_in_background=false, so every title is written in this turn; pass exactly these ids and don't widen or narrow the list. Its classifiers read the files in parallel and title them with ${templateOf(
      ctx.settings
    )}, so don't read files, skills or the tree before it.`,
    `Its result's needs_work lists the files still without a good title (one two files would share, an upload name left) and why. Only if it lists any${
      instructions
        ? ", or a title in its titles list doesn't follow the firm's title instructions below (they win over the template)"
        : ""
    }: read the needs_work files with read_file_text (max_chars=3000), all in one step as parallel calls, then write every fix in one set_exhibit_titles(titles=[{node_id, title}]) call. Make a shared title distinct with what tells the documents apart (date, author, issuer, identifier or a short qualifier); never add a number like "(2)". Don't list the tree, and don't call parse_files or read_document.`,
    instructions
      ? `A line of the instructions for a kind of document applies to that kind; fill a format's fields from what the title already says, {description} being a short description of the document; instructions written in words are followed as written.\nFirm title instructions:\n"""\n${instructions}\n"""\n`
      : "",
    `Keep the exhibit labels, and keep titles a person explicitly kept.${noteOf(
      ctx
    )}`,
    RETITLE_REPORT,
    RETITLE_END
  ]
    .filter(Boolean)
    .join(" ");
};

const SCOPE_CHARS = 60;

/**
 * A scope clause shortened for one line: up to its first ";", without
 * parentheticals, a leading "the" or "of/in Case Files".
 */
const shortScope = (scope: string): string => {
  const short = scope
    .split(";")[0]
    .replace(/\s*\([^)]*\)/g, "")
    .replace(/^the\s+/i, "")
    .replace(/\s+(?:of|in) Case Files\b/, "")
    .trim();
  const line = short.charAt(0).toUpperCase() + short.slice(1);
  return line.length > SCOPE_CHARS
    ? `${line.slice(0, SCOPE_CHARS - 1).trimEnd()}…`
    : line;
};

const RESEARCH_BRIEF_TOPICS = 2;

/** "5 topics: Journal Standing, Award Standing, …"; a notice's count as is. */
const researchBrief = (scope: string): string => {
  const at = scope.indexOf(": ");
  if (at < 0) return shortScope(scope.split(", read against")[0]);
  const names = scope.slice(at + 2).split(", ");
  const shown = names.slice(0, RESEARCH_BRIEF_TOPICS).join(", ");
  return `${scope.slice(0, at)}: ${shown}${
    names.length > RESEARCH_BRIEF_TOPICS ? ", …" : ""
  }`;
};

const keyList = (topics: ResearchTopic[]) =>
  `[${topics.map((t) => JSON.stringify(t.factKey)).join(", ")}]`;

const titles = (topics: ResearchTopic[]) =>
  topics.map((t) => t.title).join(", ");

/** One rerun_analysis call per topic not already running; the running ones are left alone. */
const rerunCalls = (label: string, topics: ResearchTopic[]): string => {
  const start = topics.filter((t) => t.status !== "running");
  const running = topics.filter((t) => t.status === "running");
  const calls = start.length
    ? `call update_key_fact(key, action="rerun_analysis") for each of keys ${keyList(
        start
      )}`
    : "";
  const left = running.length
    ? `${titles(running)} ${
        running.length === 1 ? "is" : "are"
      } already running, so don't start ${
        running.length === 1 ? "it" : "them"
      } again`
    : "";
  return `${label} (${titles(topics)}): ${[calls, left]
    .filter(Boolean)
    .join("; ")}.`;
};

const memoCall = (question: string, ids: string[]) =>
  `start_research(question=${JSON.stringify(
    question
  )}, topic_ids=${JSON.stringify(ids)})`;

const runOf = (topics: ResearchTopic[], ...runs: ResearchTopic["run"][]) =>
  topics.filter((t) => runs.includes(t.run));

/** "3 notice issues", "5 topics: A, B, …", "2 notice issues and 1 topic: A". */
const researchScope = (topics: ResearchTopic[]): string => {
  const issues = runOf(topics, "notice", "strategy");
  const rest = runOf(topics, "lookup", "analysis", "reputation", "memo");
  if (topics.some((t) => t.run === "read-notice"))
    return rest.length
      ? `Read the notice, then ${plural(rest.length, "topic")}: ${rest
          .map((t) => t.title)
          .join(", ")}`
      : "Read the notice";
  const counted = issues.length
    ? plural(issues.filter((t) => t.run === "notice").length, "notice issue") +
      (issues.some((t) => t.run === "strategy") ? " with the strategy" : "")
    : "";
  if (!rest.length) return counted;
  const list = `${plural(rest.length, "topic")}: ${rest
    .map((t) => t.title)
    .join(", ")}`;
  return counted ? `${counted} and ${list}` : list;
};

/** The reputation pass's one question; a single topic asks its own. */
const reputationQuestion = (topics: ResearchTopic[]) =>
  topics.length === 1 && topics[0].question
    ? topics[0].question
    : `How established and selective are the sources of the case's evidence (${titles(
        topics
      )})?`;

const researchSteps = (topics: ResearchTopic[]): string[] => {
  const steps: string[] = [];
  const reading = topics.some((t) => t.run === "read-notice");
  if (reading)
    steps.push(
      "First find the case's notice (the RFE, NOID or NOIR) in Case Files and read it with read_notice; if more than one file could be it, ask me which before reading. Then tell me the issues it raises."
    );
  const calls: string[] = [];
  const issues = runOf(topics, "notice", "strategy");
  if (issues.length) calls.push(rerunCalls("Notice issues", issues));
  const analyses = runOf(topics, "analysis");
  if (analyses.length) calls.push(rerunCalls("Analyses", analyses));
  const reputation = runOf(topics, "reputation");
  if (reputation.length)
    calls.push(
      `Reputation (${titles(reputation)}): ${
        reputation.length > 1 ? "one standing pass for all of them, " : ""
      }${memoCall(
        reputationQuestion(reputation),
        reputation.map((t) => t.id)
      )}.`
    );
  const memos = runOf(topics, "memo");
  if (memos.length)
    calls.push(
      `Memos, one call each with the question exactly as written: ${memos
        .map((t) => memoCall(t.question ?? t.title, [t.id]))
        .join("; ")}.`
    );
  if (calls.length)
    steps.push(
      `${
        reading ? "Then make" : "Make"
      } all of these calls together, in parallel in one step. ${calls.join(
        " "
      )}`
    );
  const lookups = runOf(topics, "lookup");
  if (lookups.length)
    steps.push(
      `Lookups (${titles(
        lookups
      )}): report each value and any flag on it from the Case Insights block; don't call key_facts for them or wait. One still checking or needing input is reported as such; never work out a value yourself.`
    );
  return steps;
};

const researchBody = (ctx: PromptContext): string => {
  const topics = ctx.research?.topics ?? [];
  const tail = `Make the calls before anything else: don't plan, read skills, the case summary or other key facts, and don't poll the runs; each runs in the background and wakes you when it's done. Then tell me in a line what started. Don't change any case files.${noteOf(
    ctx
  )}`;
  if (!topics.length)
    return (
      `Scope: the ${visaOf(
        ctx
      )} criteria this case argues, read against the evidence in Case Files. ` +
      `Start one research memo with start_research on which criteria the evidence supports best and where it's thin, citing AAO decisions, the USCIS Policy Manual and the governing regulations. ${tail}`
    );
  return [
    `Scope: ${researchScope(topics)}.`,
    ...researchSteps(topics),
    tail
  ].join(" ");
};

/** The checked work products, named exactly, for one assemble call. */
const packetFrontMatter = (ctx: PromptContext) => {
  const packet = ctx.packet ?? {
    frontMatter: [] as string[],
    includeEvidence: true,
    coverLetterFirst: true
  };
  return `Pass generate_packet exactly these arguments, one call: front_matter=${JSON.stringify(
    packet.frontMatter
  )}, include_evidence=${packet.includeEvidence}, cover_letter_first=${
    packet.coverLetterFirst
  }. Do not call list_drafts or list_forms to add more ids. If it skips an id, name it and stop; do not call generate_packet again. The build writes an exhibit list that has no document yet; name each part in front_matter_skipped with its reason.`;
};

const packetBody = (ctx: PromptContext) => {
  const { packetSettings } = ctx.settings;
  const front = packetFrontMatter(ctx);
  return [
    "Scope: the checked work products, then the included exhibits.",
    packetSettings
      ? `First read the case's saved packet settings with get_packet, once. ${front} If they're still CaseBlink's defaults, pass the firm's packet defaults to generate_packet as divider=${JSON.stringify(
          packetSettings.divider
        )} and pagination=${JSON.stringify(
          packetSettings.pagination
        )}. Otherwise pass neither, so the case's own settings stand; never change packet settings the case already set.`
      : `${front} Don't read the tree, skills or the case summary, and pass it no divider or pagination, so the case's saved packet settings stand; never change them.`,
    "Build with generate_packet in that order: front matter first, then the exhibits in label order with tabs and page numbers when include_evidence is true.",
    "A form that isn't filled has no PDF to print, so leave it out, don't fill it in this turn, and name it in your reply.",
    "Don't change what's included or edit any PDF. If letters or forms are still generating, wait for them with check_run first.",
    `If it refuses because files still have upload names, say which and stop.${noteOf(
      ctx
    )}`
  ]
    .filter(Boolean)
    .join(" ");
};

const EXHIBIT_LIST_END =
  "After that reply, the turn ends. Don't move, rename, retitle, renumber or delete files, and don't edit the list's text, draft letters, build the packet or update Case Insights in this turn.";

/**
 * The case's `exhibit_list` draft, generated the way the drafting skill
 * routes it: no format and no instructions, so it renders from the tree (or
 * keeps the firm's same-visa list form). A list edited since is never
 * overwritten here.
 */
const exhibitListBody = (ctx: PromptContext) => {
  const note = ctx.note.trim();
  return (
    [
      "Scope: the case's exhibit list, every included exhibit with its label and title in packet order, from the labels Assign Exhibit Numbers set.",
      'Call list_drafts(draft_type="exhibit_list") first, once. If the case has no exhibit list draft, create it with create_draft(draft_type="exhibit_list").',
      `Then call generate_draft(draft_id) on it once, with no format, columns or instructions${
        note
          ? ', unless the note below asks for something the standard list can\'t do (a description column, other caption or heading wording): then pass format="custom" with the note as instructions'
          : ""
      }${
        ctx.chained
          ? "; I'm waiting for it because the run goes on next, so pass run_in_background=false"
          : ""
      }. Don't read the tree, files or skills before it.`,
      "If it says the list already matches the tree, say so and stop. If it says the list was changed since it was generated, don't pass overwrite: say so and stop.",
      "Then reply in one or two sentences: that the exhibit list is ready to review in the Exhibit List section, or that its drafting run started. If its suggestions list files still named as uploaded, say how many with two examples.",
      EXHIBIT_LIST_END
    ].join(" ") + (note ? `\nNote for this case:\n"""\n${note}\n"""\n` : "")
  );
};

type WorkflowSpec = Omit<WorkflowDef, "prompt"> & {
  /** The prompt after its "Title (template vN)." head. */
  body: (ctx: PromptContext) => string;
};

/**
 * Catalog order is the board and the Run Everything queue. The main lane
 * runs Retitle, then Organize, then Assign Exhibit Numbers, then Check Case
 * Insights and Check Inconsistencies, then Draft Letters, then the packet.
 * Research and Forms start on their own lanes. Draft Exhibit List is
 * optional: it runs only from its own card.
 */
const SPECS: readonly WorkflowSpec[] = [
  {
    id: "retitle",
    group: "exhibits",
    kind: "casey",
    title: "Retitle Exhibits",
    description: "Gives each exhibit a descriptive title from its contents.",
    icon: PenLine,
    template: "retitle-exhibits",
    version: 12,
    skillId: null,
    dependsOn: [],
    after: [],
    lane: "main",
    summary: (s) =>
      s.titleInstructions
        ? "Titles: Firm title instructions"
        : s.titleTemplateIsFirm
        ? `Template: ${s.titleTemplate}`
        : `Titles: Per-document formats${DEFAULT_MARK}`,
    briefSince: 7,
    brief: (scope) => {
      const only = /^only the (\d+ files?) still named as uploaded$/.exec(
        scope
      );
      if (only) return `${only[1]} with upload names`;
      const plain = scope.replace(/, still named as uploaded$/, "");
      return /^(?:\d+ new files?|all \d+ files?, replacing titles)$/.test(plain)
        ? plain
        : shortScope(scope);
    },
    body: retitleBody
  },
  {
    id: "organize",
    group: "documents",
    kind: "casey",
    title: "Organize Into Folders",
    description:
      "Reorganizes all case files into your firm's folders for this visa, moving filed ones too.",
    icon: FolderTree,
    template: "organize-files",
    version: 9,
    skillId: null,
    dependsOn: [],
    after: [],
    lane: "main",
    summary: (s) =>
      `Folders: your firm's ${s.visa || "visa"} structure · Applies with Undo`,
    briefSince: 5,
    brief: (scope) => {
      const all = /^all (\d+ files?) in Case Files\b/.exec(scope);
      return all ? `all ${all[1]}` : shortScope(scope);
    },
    body: organizeBody
  },
  {
    id: "number",
    group: "documents",
    kind: "direct",
    title: "Assign Exhibit Numbers",
    description:
      "The label style exhibits number in. Labels follow folder order and citations keep up.",
    icon: ListOrdered,
    template: "number-exhibits",
    version: 4,
    skillId: null,
    dependsOn: ["organize"],
    after: ["organize"],
    lane: "main",
    summary: (s) =>
      `Labels follow folder order · Letters cite as ${s.citation}${
        s.citationIsFirm ? "" : DEFAULT_MARK
      }`,
    body: () => ""
  },
  {
    id: "exhibitList",
    group: "documents",
    kind: "casey",
    title: "Draft Exhibit List",
    description:
      "Drafts the exhibit list from the assigned numbers: each exhibit's number and title, in packet order, as a document you can edit.",
    icon: TableOfContents,
    template: "draft-exhibit-list",
    version: 1,
    skillId: null,
    dependsOn: ["number"],
    after: ["retitle", "organize"],
    lane: "main",
    optional: true,
    summary: () =>
      "Exhibit numbers and titles in packet order · Follows renames and reorders",
    briefSince: 1,
    brief: () => "Numbers and titles in packet order",
    body: exhibitListBody
  },
  {
    id: "insights",
    group: "analysis",
    kind: "casey",
    title: "Check Case Insights",
    description:
      "Proposes values from the files — name, dates, employer, visa facts — and leaves them for you to confirm.",
    icon: ListChecks,
    template: "check-insights",
    version: 2,
    skillId: null,
    dependsOn: [],
    after: ["organize"],
    lane: "main",
    summary: () => "Proposes values from the files for you to confirm",
    briefSince: 2,
    brief: () => "proposes values from the files",
    body: insightsBody
  },
  {
    id: "inconsistencies",
    group: "analysis",
    kind: "casey",
    title: "Check Inconsistencies",
    description:
      "Finds facts that disagree across files, insights, drafts and forms, and leaves a checklist for you to resolve.",
    icon: ListTodo,
    template: "check-inconsistencies",
    version: 1,
    skillId: null,
    dependsOn: [],
    after: ["insights"],
    lane: "main",
    summary: () => "A checklist of facts that disagree, for you to resolve",
    briefSince: 1,
    brief: () => "facts that disagree",
    body: inconsistenciesBody
  },
  {
    id: "research",
    group: "drafting",
    kind: "casey",
    title: "Case Research",
    description:
      "Researches the topics this case type calls for, with citations.",
    icon: BookOpen,
    template: "case-research",
    version: 5,
    briefSince: 2,
    skillId: null,
    dependsOn: [],
    after: [],
    lane: "research",
    summary: () =>
      "Lookups, analyses, reputation and legal authority for this case type · Runs in the background",
    brief: (scope) => researchBrief(scope),
    body: (ctx) => researchBody(ctx)
  },
  {
    id: "letters",
    group: "drafting",
    kind: "casey",
    title: "Draft Letters",
    description:
      "The letters this visa calls for, each from the template you pick.",
    icon: FileText,
    template: "draft-letters",
    version: 9,
    skillId: null,
    dependsOn: ["number"],
    after: ["retitle", "research"],
    lane: "main",
    summary: (s) =>
      `${
        s.defaultTemplates.length
          ? `${s.defaultTemplates.length} firm default ${
              s.defaultTemplates.length === 1 ? "template" : "templates"
            }`
          : "Petition, support and cover letters"
      } · Cites as ${s.citation}${s.citationIsFirm ? "" : DEFAULT_MARK}`,
    briefSince: 7,
    brief: (scope) =>
      /^the petition letter\b/.test(scope)
        ? "Petition, support and cover letters"
        : /^the letters\b/.test(scope)
        ? "The letters this filing needs"
        : scope,
    body: (ctx) =>
      `${
        ctx.letters.length
          ? `Scope: ${lettersLine(ctx.letters)}. ` +
            `Draft exactly these letter types and no others: ${ctx.letters
              .map(letterSource)
              .join(
                "; "
              )}. Call list_drafts once first and reuse drafts of these types that exist. `
          : `Scope: the letters a ${visaOf(
              ctx
            )} filing needs; call list_drafts once first and reuse drafts that exist. ` +
            `${templatesOf(ctx)} `
      }Create what's missing with create_draft(draft_type=…), all in one step as parallel calls. ` +
      `Then start every letter in one step, as parallel generate_draft calls, one per letter, so they're written side by side${
        ctx.chained
          ? "; I'm waiting for them because the packet is built next, so pass run_in_background=false on each"
          : ""
      }. ` +
      "Keep each call's instructions to what the drafting agent can't read itself (the template, the citation style, this case's note): it reads the case files and Case Insights on its own, so don't read them first. " +
      `Cite exhibits as ${citationOf(
        ctx.settings
      )} with their current labels. ` +
      `Don't overwrite text someone edited; list what you skipped.${noteOf(
        ctx
      )}`
  },
  {
    id: "forms",
    group: "drafting",
    kind: "casey",
    title: "Fill Forms",
    description:
      "Fills the USCIS forms and the G-28 from intake and case files.",
    icon: ClipboardList,
    template: "fill-forms",
    version: 5,
    briefSince: 3,
    skillId: null,
    dependsOn: [],
    after: [],
    lane: "forms",
    summary: (s) =>
      `${s.visa || "Visa"} USCIS forms and G-28 · From intake and case files`,
    brief: (scope) => {
      const picked = /, one document each: (.+)$/.exec(scope)?.[1];
      if (picked) return picked;
      const visa = /forms an? (.+?) filing needs/.exec(scope)?.[1];
      return visa && visa !== "this visa"
        ? `${visa} USCIS forms and the G-28`
        : "USCIS forms and the G-28";
    },
    body: (ctx) =>
      `${ctx.forms?.length ? pickedForms(ctx.forms) : visaForms(ctx)}` +
      `${
        ctx.chained
          ? "; I'm waiting for them, so pass block_until_seconds=600 on each"
          : ""
      }. fill_form picks its own sources from the intake answers and case files, so don't read them first. ` +
      "Leave anything you can't source blank and list what's missing. " +
      `Don't draft letters or build the packet in this turn.${noteOf(ctx)}`
  },
  {
    id: "packet",
    group: "filing",
    kind: "casey",
    title: "Assemble Filing Packet",
    description:
      "Merges forms, letters and exhibits with tabs and page numbers.",
    icon: Package,
    template: "assemble-packet",
    version: 11,
    skillId: null,
    dependsOn: ["retitle", "number", "letters", "forms"],
    after: ["research"],
    lane: "main",
    summary: (s) =>
      `The checked work products, then exhibits · Case settings, else ${
        s.packetSettings ? "the firm's packet defaults" : "CaseBlink's"
      }`,
    briefSince: 5,
    brief: () => "The checked work products and exhibits",
    body: packetBody
  }
];

/** "Organize Into Folders (organize-files v4).": how every prompt starts. */
const promptHead = (def: Pick<WorkflowDef, "title" | "template" | "version">) =>
  `${def.title} (${def.template} v${def.version}).`;

export const WORKFLOWS: readonly WorkflowDef[] = SPECS.map(
  ({ body, ...def }) => ({
    ...def,
    prompt: (ctx) => `${promptHead(def)} ${body(ctx)}`
  })
);

export const WORKFLOW_IDS: readonly WorkflowId[] = WORKFLOWS.map((w) => w.id);

/** The steps Run Everything runs and progress counts: every one not optional. */
export const REQUIRED_WORKFLOW_IDS: readonly WorkflowId[] = WORKFLOWS.filter(
  (w) => !w.optional
).map((w) => w.id);

export const isOptional = (id: WorkflowId): boolean =>
  WORKFLOWS.some((w) => w.id === id && w.optional);

export const workflowById = (id: WorkflowId): WorkflowDef =>
  WORKFLOWS.find((w) => w.id === id) as WorkflowDef;

/** "organize-files v9 · Agent Mode" */
export const promptMeta = (def: WorkflowDef): string =>
  [
    `${def.template} v${def.version}`,
    def.skillId ? `Automation ${def.skillId.replace(/^builtin:/, "")}` : null,
    "Agent Mode"
  ]
    .filter(Boolean)
    .join(" · ");

/** A workflow prompt read back from a chat message. */
export type WorkflowPromptMatch = {
  def: WorkflowDef;
  /** The version the head names; another than the catalog's reads generically. */
  version: number;
  /** "37 files at the top level"; null when the prompt's scope can't be read. */
  scope: string | null;
  /** "Organize Into Folders · 37 files at the top level" */
  line: string;
};

const HEAD = /^\s*([^()\n]{1,60}?) \(([a-z0-9-]{1,40}) v(\d{1,3})\)\.(?:\s|$)/;
/** A workflow name: Title Case words, so "Please run …" is someone typing. */
const TITLE_CASE = /^[A-Z][\w-]*(?: [A-Z][\w-]*)*$/;
/**
 * The "Scope: …" clause, up to the end of its sentence. Null when the text
 * stops first (a clipped title or preview), so a half clause never shows.
 */
const scopeClause = (text: string): string | null => {
  const at = text.indexOf("Scope: ");
  if (at < 0) return null;
  const rest = text.slice(at + "Scope: ".length);
  const end = rest.search(/\.(?:\s|$)|;/);
  return end > 0 ? rest.slice(0, end) : null;
};

/**
 * Recognize a workflow prompt by its head, the template id or the title, so
 * an older version still reads as its workflow. Null for anything else.
 */
export const readWorkflowPrompt = (
  text: string
): WorkflowPromptMatch | null => {
  const head = HEAD.exec(text);
  if (!head) return null;
  const [, title, template, version] = head;
  const def =
    WORKFLOWS.find((w) => w.title === title) ??
    (TITLE_CASE.test(title)
      ? WORKFLOWS.find((w) => w.template === template)
      : undefined);
  if (!def) return null;
  const v = Number(version);
  const clause = scopeClause(text.slice(head[0].length));
  let scope: string | null = null;
  if (clause)
    scope =
      (def.brief && v >= (def.briefSince ?? def.version) && v <= def.version
        ? def.brief(clause)
        : shortScope(clause)) || null;
  return {
    def,
    version: v,
    scope,
    line: scope ? `${def.title} · ${scope}` : def.title
  };
};

/** A message's text as titles and previews show it: a workflow prompt as its line. */
export const workflowDisplayText = (text: string): string =>
  readWorkflowPrompt(text)?.line ?? text;
